import PropTypes from 'prop-types';
import React, { useMemo, useCallback, useContext, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { emptyArray } from 'ui-components/default-props/empty';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { GuidService } from 'ui-components/services/guid-service';
import { NotificationService, NOTIFICATION_TYPE_ERROR, NOTIFICATION_TYPE_SUCCESS } from 'ui-components/services/notification-service';
import { useSellerShipStationFulfillmentProvider } from '../../../../../hooks/use-shipstation-fulfillment-whitelabel-enabled';
import { usePaging } from '../../../../../hooks/use-paging';
import { LogService } from '../../../../../services/log-service';
import { calculatePagingStats } from '../../../../../services/pagination-service';
import { Workflows } from '../../../../../tracking/tracking-constants';
import { useTrackWorkflow, WorkflowSteps } from '../../../../../tracking/use-workflow-tracking';
import {
  AsnTableViewSegmentEvents,
  useAsnTableViewSegmentTracking,
} from '../../../../settings/fulfillment-providers/hooks/use-asn-table-view-segment-tracking';
import { cancelAsn, deleteAsn, getAsnWithId, getPaginatedAsns, createNewAsn } from '../../../fulfillments.api';
import { initialAdvancedShippingNotice } from '../../constants';
import { messages } from '../../messages';

const AdvancedShippingNoticeGridContext = React.createContext();
const { Consumer, Provider } = AdvancedShippingNoticeGridContext;

const DEFAULT_PAGE_START = 1;
const DEFAULT_PAGE_SIZE = 25;
/**
 * Provider component for the Advanced Shipping Notice Grid context
 * Manages state and functions related to the grid data and operations
 * @param {object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @param {(AdvancedShippingNotice) => {}} [props.setInitialAsn] - Initial data for the grid
 * @param {() => {}} [props.open] - Initial loading state
 * @returns {JSX.Element} The provider component
 */
const AdvancedShippingNoticeGridProvider = ({ children, setInitialAsn, open }) => {
  const { sellerFulfillmentProviderId } = useSellerShipStationFulfillmentProvider() || {};
  const trackTableViewEvent = useAsnTableViewSegmentTracking();
  const trackAsnWorkflow = useTrackWorkflow(Workflows.ADVANCED_SHIPPING_NOTICE_FLOW);

  const [isLoading, setIsLoading] = useAsyncSafeState(false);
  const [gridData, setGridData] = useAsyncSafeState(emptyArray);
  const [recordCount, setRecordCount] = useAsyncSafeState(0);

  // Add pagination hooks
  const { page, pageSize, updatePageInformation } = usePaging(DEFAULT_PAGE_START, DEFAULT_PAGE_SIZE);
  const [totalPages, setTotalPages] = useState(1);
  const [viewStartingCount, setViewStartingCount] = useState(0);
  const [viewEndingCount, setViewEndingCount] = useState(0);

  // Add filter state
  const [appliedFilters, setAppliedFilters] = useState(emptyArray);

  // Update pagination stats when data changes
  useEffect(() => {
    const pageStats = calculatePagingStats(page, pageSize, recordCount);
    setTotalPages(pageStats.totalPages);
    setViewStartingCount(pageStats.viewStartingCount);
    setViewEndingCount(pageStats.viewEndingCount);
  }, [page, pageSize, recordCount]);

  const onSelectAsn = useCallback(
    async (asnId) => {
      setIsLoading(true);
      trackAsnWorkflow(WorkflowSteps.CONTINUE);
      try {
        const selectedAsn = await getAsnWithId(sellerFulfillmentProviderId, asnId);
        setInitialAsn(selectedAsn);
        open();
      } catch {
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_ERROR,
          title: <FormattedMessage {...messages.errorNotificationGetASNsTitle} />,
          content: <FormattedMessage {...messages.errorNotificationGetASNsMessage} />,
        });
      }
      setIsLoading(false);
    },
    [open, sellerFulfillmentProviderId, setInitialAsn, setIsLoading, trackAsnWorkflow],
  );

  const loadAsnGridData = useCallback(async () => {
    setIsLoading(true);
    try {
      const { items, totalRecords } = await getPaginatedAsns(sellerFulfillmentProviderId, {
        page,
        pageSize,
        statusList: appliedFilters.map((filter) => filter.criterionArgumentValue).join(','),
      });
      const mappedItems = items.map((item) => ({ ...item, id: item.asnId }));
      setGridData(mappedItems);
      setRecordCount(totalRecords);
    } catch {
      NotificationService.showNotification({
        notificationType: NOTIFICATION_TYPE_ERROR,
        title: <FormattedMessage {...messages.errorNotificationGetASNsTitle} />,
        content: <FormattedMessage {...messages.errorNotificationGetASNsMessage} />,
      });
    }
    setIsLoading(false);
  }, [sellerFulfillmentProviderId, setGridData, setIsLoading, setRecordCount, page, pageSize, appliedFilters]);

  const onDeleteAsn = useCallback(
    async (asnId) => {
      setIsLoading(true);
      trackTableViewEvent(AsnTableViewSegmentEvents.DELETE_ASN, { asnId });
      try {
        await deleteAsn(sellerFulfillmentProviderId, asnId);
        await loadAsnGridData();
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_SUCCESS,
          title: <FormattedMessage {...messages.successNotificationDeleteTitle} />,
          content: <FormattedMessage {...messages.successNotificationDeleteMessage} />,
        });
      } catch {
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_ERROR,
          title: <FormattedMessage {...messages.errorNotificationDeleteTitle} />,
          content: <FormattedMessage {...messages.errorNotificationDeleteMessage} />,
        });
      } finally {
        setIsLoading(false);
      }
    },
    [sellerFulfillmentProviderId, setIsLoading, trackTableViewEvent, loadAsnGridData],
  );

  const onDuplicateAsn = useCallback(
    async (asnId) => {
      trackTableViewEvent(AsnTableViewSegmentEvents.DUPLICATE_ASN, { asnId });
      try {
        setIsLoading(true);
        const asnToDuplicate = await getAsnWithId(sellerFulfillmentProviderId, asnId);

        const duplicatedBoxes = (asnToDuplicate.boxes || []).map((box) => ({
          ...box,
          boxId: null,
          boxConfigurationId: box.boxConfigurationId,
        }));

        const newGuidPlaceholder = GuidService.newGuid();
        const duplicatedAsnPayload = {
          ...initialAdvancedShippingNotice,
          receivingCenter: asnToDuplicate.receivingCenter,
          clientId: asnToDuplicate.clientId,
          packageType: asnToDuplicate.packageType,
          purchaseOrderNumber: newGuidPlaceholder,
          expectedProducts: (asnToDuplicate.expectedProducts || []).map((product) => ({
            ...product,
            receivedQuantity: 0,
            stowedQuantity: 0,
          })),
          boxes: duplicatedBoxes,
          parcelConfigurations: asnToDuplicate.parcelConfigurations || [],
          freightConfiguration: asnToDuplicate.freightConfiguration || initialAdvancedShippingNotice.freightConfiguration,
          expectedArrivalDate: asnToDuplicate.expectedArrivalDate || '',
        };

        const createdAsn = await createNewAsn(sellerFulfillmentProviderId, duplicatedAsnPayload);

        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_SUCCESS,
          title: <FormattedMessage {...messages.successNotificationDuplicateTitle} />,
          content: (
            <FormattedMessage
              {...messages.successNotificationDuplicateMessage}
              values={{ purchaseOrderNumber: createdAsn.purchaseOrderNumber }}
            />
          ),
        });

        setInitialAsn(createdAsn);
        open();
      } catch (err) {
        LogService.logError(new Error('Error duplicating ASN', { cause: err }), { asnId });
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_ERROR,
          title: <FormattedMessage {...messages.errorNotificationDuplicateTitle} />,
          content: <FormattedMessage {...messages.errorNotificationDuplicateMessage} />,
        });
      } finally {
        setIsLoading(false);
      }
    },
    [open, sellerFulfillmentProviderId, setInitialAsn, setIsLoading, trackTableViewEvent],
  );

  const onCancelAsn = useCallback(
    async (asnId) => {
      setIsLoading(true);
      trackTableViewEvent(AsnTableViewSegmentEvents.CANCEL_ASN, { asnId });
      try {
        await cancelAsn(sellerFulfillmentProviderId, asnId);
        await loadAsnGridData();
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_SUCCESS,
          title: <FormattedMessage {...messages.successNotificationCancelTitle} />,
          content: <FormattedMessage {...messages.successNotificationCancelMessage} />,
        });
      } catch {
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_ERROR,
          title: <FormattedMessage {...messages.errorNotificationCancelTitle} />,
          content: <FormattedMessage {...messages.errorNotificationCancelMessage} />,
        });
      } finally {
        setIsLoading(false);
      }
    },
    [sellerFulfillmentProviderId, setIsLoading, trackTableViewEvent, loadAsnGridData],
  );

  // Handle filter changes
  const onFiltersChanged = useCallback(
    (newFilters) => {
      setAppliedFilters(newFilters);
      // Reset to first page when filters change
      updatePageInformation(1, pageSize);
    },
    [pageSize, updatePageInformation],
  );

  // Clear all filters
  const clearFilters = useCallback(() => {
    setAppliedFilters(emptyArray);
  }, []);

  useEffect(() => {
    loadAsnGridData();
  }, [loadAsnGridData, page, pageSize, appliedFilters]);

  const value = useMemo(
    () => ({
      isLoading,
      gridData,
      recordCount,
      onSelectAsn,
      onDeleteAsn,
      onCancelAsn,
      onDuplicateAsn,
      // Pagination related values
      page,
      pageSize,
      totalPages,
      viewStartingCount,
      viewEndingCount,
      updatePageInformation,
      // Filter related values
      appliedFilters,
      onFiltersChanged,
      clearFilters,
      loadAsnGridData,
    }),
    [
      isLoading,
      gridData,
      recordCount,
      onSelectAsn,
      onDeleteAsn,
      onCancelAsn,
      onDuplicateAsn,
      // Pagination related dependencies
      page,
      pageSize,
      totalPages,
      viewStartingCount,
      viewEndingCount,
      updatePageInformation,
      // Filter related dependencies
      appliedFilters,
      onFiltersChanged,
      clearFilters,
      loadAsnGridData,
    ],
  );

  return <Provider value={value}>{children}</Provider>;
};

AdvancedShippingNoticeGridProvider.propTypes = {
  children: PropTypes.node.isRequired,
  initialData: PropTypes.array,
  initialLoading: PropTypes.bool,
};

/**
 * Custom hook to use the Advanced Shipping Notice Grid context
 * @returns {object} The context value
 * @throws {Error} If used outside of a AdvancedShippingNoticeGridProvider
 */
export const useAsnGridContext = () => {
  const context = useContext(AdvancedShippingNoticeGridContext);

  if (context === undefined) {
    throw new Error('useAdvancedShippingNoticeGrid must be used within a AdvancedShippingNoticeGridProvider');
  }

  return context;
};

export { Consumer, AdvancedShippingNoticeGridContext as Context, AdvancedShippingNoticeGridProvider as Provider };
